import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morph } from "@theme/morph";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @typedef {object} UpdateConfig
 * @property {number} line - 1-based line index (used for error display / single-line updates).
 * @property {string} action - "change" | "clear".
 * @property {number} [quantity] - Quantity for single-line updates via cart/change.
 * @property {Record<string, number>} [updates] - Line-item key → quantity, for atomic multi-line updates via cart/update.
 */

/**
 * A custom element that displays a cart items component.
 *
 * Only morphs ITSELF on updates, never the whole section. In the drawer the
 * section is the header, and re-rendering it breaks the open <dialog>'s
 * modal state (drawer ends up inside the header / page stays locked).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** Number of in-flight requests; the component stays disabled while > 0. */
  #pendingRequests = 0;

  /** Serializes cart requests so they never overlap. @type {Promise<void>} */
  #requestQueue = Promise.resolve();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Returns the gift row linked to a main (non-gift) row, if any.
   * @param {HTMLElement | undefined} row
   * @returns {HTMLElement | undefined}
   */
  #getLinkedGiftRow(row) {
    if (!row) return undefined;
    const bundleId = row.dataset.bundleId;
    if (!bundleId || row.dataset.isGift === "true") return undefined;

    return this.refs.cartItemRows.find(
      (r) => r.dataset.bundleId === bundleId && r.dataset.isGift === "true",
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const row = this.refs.cartItemRows[line - 1];
    const giftRow = this.#getLinkedGiftRow(row);
    const mainKey = row?.dataset.key;
    const giftKey = giftRow?.dataset.key;

    if (mainKey && giftKey) {
      this.updateQuantity({
        line,
        action: "change",
        updates: { [mainKey]: quantity, [giftKey]: quantity },
      });
    } else {
      this.updateQuantity({ line, quantity, action: "change" });
    }

    const textComponent = /** @type {TextComponent | undefined} */ (
      row?.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const row = this.refs.cartItemRows[line - 1];
    if (!row) return;

    const giftRow = this.#getLinkedGiftRow(row);
    const mainKey = row.dataset.key;
    const giftKey = giftRow?.dataset.key;

    const rowsToRemove = [
      row,
      ...this.refs.cartItemRows.filter((r) => r.dataset.parentKey === row.dataset.key),
    ];
    if (giftRow) rowsToRemove.push(giftRow);

    if (mainKey && giftKey) {
      this.updateQuantity({
        line,
        action: "clear",
        updates: { [mainKey]: 0, [giftKey]: 0 },
      });
    } else {
      this.updateQuantity({ line, quantity: 0, action: "clear" });
    }

    rowsToRemove.forEach((r) => {
      const remove = () => r.remove();
      if (prefersReducedMotion()) return remove();

      r.style.setProperty("--row-height", `${r.clientHeight}px`);
      r.classList.add("removing");
      onAnimationEnd(r, remove);
    });
  }

  /**
   * Queues a cart update so requests never overlap.
   * @param {UpdateConfig} config
   * @returns {Promise<void>}
   */
  updateQuantity(config) {
    const run = () => this.#performUpdate(config);
    this.#requestQueue = this.#requestQueue.then(run, run);
    return this.#requestQueue;
  }

  /**
   * Sends the cart request and re-renders this component.
   * @param {UpdateConfig} config
   */
  async #performUpdate(config) {
    this.#disableCartItems();
    let marker;

    try {
      marker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

      const sectionsToUpdate = new Set([this.sectionId]);
      document.querySelectorAll("cart-items-component").forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sections = Array.from(sectionsToUpdate).join(",");
      const sections_url = window.location.pathname;

      const url = config.updates
        ? Theme.routes.cart_update_url || "/cart/update.js"
        : Theme.routes.cart_change_url;

      const body = JSON.stringify(
        config.updates
          ? { updates: config.updates, sections, sections_url }
          : { line: config.line, quantity: config.quantity, sections, sections_url },
      );

      this.refs.cartTotal?.shimmer();

      const response = await fetch(url, fetchConfig("json", { body }));
      const parsed = JSON.parse(await response.text());

      resetShimmer(this);

      if (parsed.errors || !response.ok) {
        this.#handleCartError(config.line, {
          errors: parsed.errors || parsed.description || parsed.message || "Cart error",
        });
        return;
      }

      const sectionHtml = parsed.sections?.[this.sectionId];
      if (!sectionHtml) {
        await this.#renderSelf();
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHtml, "text/html");
      const countText = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = countText ? parseInt(countText, 10) : 0;

      this.#updateQuantitySelectors(parsed);

      this.dispatchEvent(
        new CartUpdateEvent(parsed, this.sectionId, {
          itemCount: newCartItemCount,
          source: "cart-items-component",
          sections: parsed.sections,
        }),
      );

      this.#morphSelf(newSectionHTML);
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      if (marker) cartPerformance.measureFromMarker(marker);
    }
  }

  /**
   * Morphs only this <cart-items-component> from a rendered section,
   * leaving the rest of the section (header, dialog) untouched.
   * @param {Document | string} source - Parsed section document or raw section HTML.
   */
  #morphSelf(source) {
    const doc =
      typeof source === "string" ? new DOMParser().parseFromString(source, "text/html") : source;

    const next =
      doc.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`) ||
      doc.querySelector("cart-items-component");

    if (!next) return;

    morph(this, next);
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Fetches this section fresh and morphs only this component.
   */
  async #renderSelf() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("section_id", this.sectionId);
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
      this.#morphSelf(await response.text());
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error. Never throws, so a missing ref can't break the cart.
   * @param {number} line - The line.
   * @param {{ errors: string }} parsedResponseText
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantityInput = this.refs.quantitySelectors?.[line - 1]?.querySelector("input");
    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove("hidden");
    } else {
      console.warn("Cart error:", parsedResponseText.errors);
    }
  };

  /**
   * Handles cart updates from elsewhere (product form add, other cart components).
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      this.#renderSelf();
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#morphSelf(cartItemsHtml);
    } else {
      this.#renderSelf();
    }
  };

  /**
   * Disables the cart items (reference-counted).
   */
  #disableCartItems() {
    this.#pendingRequests++;
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items once no requests are in flight.
   */
  #enableCartItems() {
    this.#pendingRequests = Math.max(0, this.#pendingRequests - 1);
    if (this.#pendingRequests === 0) {
      this.classList.remove("cart-items-disabled");
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`,
      );

      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;

        input.setAttribute("data-cart-quantity", item.quantity.toString());

        if ("updateCartQuantity" in selector && typeof selector.updateCartQuantity === "function") {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll("cart-quantity-selector-component")) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error("Section id missing");
    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
